// pattern: Functional Core

/**
 * Quiz Engine - Pure business logic for quiz-taking
 * Functional Core - no side effects
 */

import type { StoredQuestion } from './question-model.ts';

/** Check if the selected answer index is correct */
export function checkAnswer(question: StoredQuestion, selectedIndex: number): boolean {
  return question.correctAnswerIndex === selectedIndex;
}

/** Calculate score as a percentage (0-100) */
export function calculateScore(correctCount: number, totalQuestions: number): number {
  if (totalQuestions === 0) return 0;
  return Math.round((correctCount / totalQuestions) * 100);
}

/**
 * Select questions for a quiz attempt.
 * - structured mode: returns all questions in original order
 * - pool mode: randomly samples `poolSize` questions and shuffles
 */
export function selectQuestions(
  questions: ReadonlyArray<StoredQuestion>,
  mode: 'structured' | 'pool',
  poolSize?: number
): Array<StoredQuestion> {
  if (mode === 'structured') {
    return [...questions];
  }

  // Pool mode: randomly sample and shuffle
  const size = poolSize ?? questions.length;
  const shuffled = shuffleArray([...questions]);
  return shuffled.slice(0, Math.min(size, shuffled.length));
}

/**
 * Fisher-Yates shuffle. Returns a new array.
 * Exported for testing; callers should prefer selectQuestions().
 */
export function shuffleArray<T>(array: Array<T>): Array<T> {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = result[i];
    if (temp !== undefined && result[j] !== undefined) {
      result[i] = result[j] as T;
      result[j] = temp;
    }
  }
  return result;
}
